import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { resolveMedia } from '../../context/SettingsContext';

export default function ProductQuickViewModal({ product, isOpen, onClose }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const images = product?.images && product.images.length > 0
    ? product.images
    : ['/assets/ganesha_idol.jpg'];

  const isSaved = product ? isInWishlist(product._id) : false;
  const isVariable = product?.productType === 'variable';
  const variants = product?.variants || [];
  const variantAttributes = product?.variantAttributes || [];

  // Initialize variant attributes
  useEffect(() => {
    if (isVariable && variantAttributes.length > 0) {
      const initial = {};
      variantAttributes.forEach((attr) => {
        if (attr.options && attr.options.length > 0) {
          initial[attr.name] = attr.options[0];
        }
      });
      setSelectedAttributes(initial);
    }
    setQuantity(1);
    setSelectedImageIndex(0);
  }, [product, isVariable, variantAttributes]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !product) return null;

  // Compute matched variant if variable
  const matchedVariant = isVariable && variants.length > 0
    ? variants.find((v) => {
        if (!v.attributes) return false;
        const attrMap = v.attributes instanceof Map ? Object.fromEntries(v.attributes) : v.attributes;
        return Object.entries(selectedAttributes).every(
          ([k, val]) => String(attrMap[k] || '').trim() === String(val || '').trim()
        );
      })
    : null;

  const currentPrice = isVariable && matchedVariant
    ? matchedVariant.price
    : Number(product.price || 0);

  const compareAtPrice = isVariable && matchedVariant
    ? matchedVariant.compareAtPrice
    : Number(product.compareAtPrice || 0);

  const discount = compareAtPrice > currentPrice
    ? Math.round(((compareAtPrice - currentPrice) / compareAtPrice) * 100)
    : 0;

  const currentStock = isVariable && matchedVariant
    ? matchedVariant.stock ?? 0
    : Number(product.stock ?? 0);

  const isOutOfStock = currentStock <= 0;

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    if (isVariable) {
      if (matchedVariant) {
        addToCart(product, quantity, matchedVariant);
      } else {
        navigate(`/product/${product.slug}`);
        onClose();
        return;
      }
    } else {
      addToCart(product, quantity);
    }
    onClose();
  };

  const handleOpenFullPage = () => {
    onClose();
    navigate(`/product/${product.slug}`);
  };

  const activeImage = images[selectedImageIndex] || images[0];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-view-title"
    >
      {/* Dark Blur Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />

      {/* Modal Card */}
      <div
        className="relative z-10 w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl border border-[#E3DDD7] shadow-2xl text-[#241914] scrollbar-thin"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3.5 top-3.5 z-20 w-8 h-8 rounded-full bg-[#FAF8F5] hover:bg-[#E8DFD8] border border-[#E0D6CC] text-[#241914] flex items-center justify-center transition-colors shadow-xs"
          title="Close details"
          aria-label="Close modal"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 sm:p-7">
          {/* Left Column: Image Gallery */}
          <div className="space-y-3">
            <div className="relative aspect-[4/5] bg-[#F5EFEB] rounded-xl overflow-hidden border border-[#E8DFD8]">
              {product.isBestSeller && (
                <span className="absolute left-3 top-3 z-10 rounded bg-[#241914] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-white shadow-xs">
                  Best Seller
                </span>
              )}
              {discount > 0 && (
                <span className="absolute left-3 top-3 z-10 rounded bg-emerald-700 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-white shadow-xs">
                  Save {discount}%
                </span>
              )}
              <img
                src={resolveMedia(activeImage)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/assets/ganesha_idol.jpg';
                }}
              />
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`relative w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                      idx === selectedImageIndex
                        ? 'border-[#C49A45] ring-2 ring-[#C49A45]/30'
                        : 'border-[#E8DFD8] opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={resolveMedia(img)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/ganesha_idol.jpg';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Product Details */}
          <div className="flex flex-col justify-between space-y-4">
            <div className="space-y-3">
              {/* Category & SKU */}
              <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-[#9D772D]">
                <span>{product.category?.name || 'Sandalwood'}</span>
                {product.sku && <span className="text-[#8B7E76] font-mono normal-case">SKU: {product.sku}</span>}
              </div>

              {/* Product Title */}
              <h2
                id="quick-view-title"
                className="font-heading text-xl sm:text-2xl font-bold leading-snug text-[#241914] hover:text-[#C49A45] transition-colors cursor-pointer"
                onClick={handleOpenFullPage}
              >
                {product.name}
              </h2>

              {/* Rating & Reviews */}
              <div className="flex items-center gap-2 text-xs">
                {product.ratingsCount > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#C49A45] font-bold">★ {Number(product.ratingsAverage || 0).toFixed(1)}</span>
                    <span className="text-[#8B7E76]">({product.ratingsCount} verified reviews)</span>
                  </div>
                ) : (
                  <span className="text-[#8B7E76] text-xs">New authentic arrival</span>
                )}
                <span className="text-[#D9D0C8]">·</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                  isOutOfStock ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {isOutOfStock ? 'Out of Stock' : `In Stock (${currentStock} left)`}
                </span>
              </div>

              {/* Price & Discounts */}
              <div className="flex items-baseline gap-3 pt-1 border-t border-[#E8DFD8] pb-2">
                <span className="font-heading text-2xl font-bold text-[#241914]">
                  ₹{Number(currentPrice).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
                {compareAtPrice > currentPrice && (
                  <span className="text-sm text-[#8B7E76] line-through">
                    ₹{Number(compareAtPrice).toLocaleString('en-IN')}
                  </span>
                )}
                {discount > 0 && (
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    Save {discount}%
                  </span>
                )}
              </div>

              {/* Short Description */}
              <p className="text-xs leading-relaxed text-[#665950] line-clamp-3">
                {product.shortDescription || product.description || '100% genuine Mysore sandalwood, ethically sourced and handcrafted by master artisans.'}
              </p>

              {/* Variable Attributes Selector (if variable) */}
              {isVariable && variantAttributes.length > 0 && (
                <div className="space-y-3 pt-2 border-t border-[#E8DFD8]">
                  {variantAttributes.map((attr) => (
                    <div key={attr.name} className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase text-[#76675E] tracking-wider block">
                        {attr.name}: <span className="text-[#241914] font-bold">{selectedAttributes[attr.name]}</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(attr.options || []).map((opt) => {
                          const isSelected = selectedAttributes[attr.name] === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setSelectedAttributes((prev) => ({ ...prev, [attr.name]: opt }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                isSelected
                                  ? 'bg-[#241914] text-white border-[#241914] shadow-xs'
                                  : 'bg-white text-[#5E514A] border-[#D9D0C8] hover:border-[#C49A45]'
                              }`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quantity Selector & Action Buttons */}
            <div className="space-y-3 pt-4 border-t border-[#E8DFD8]">
              <div className="flex items-center gap-3">
                {/* Quantity Controls */}
                <div className="flex items-center border border-[#D9D0C8] rounded-lg bg-[#FAF8F5] overflow-hidden shrink-0">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || isOutOfStock}
                    className="w-8 h-9 flex items-center justify-center text-sm font-bold text-[#5E514A] hover:bg-[#E8DFD8] transition-colors disabled:opacity-40"
                  >
                    -
                  </button>
                  <span className="w-9 text-center font-bold text-xs text-[#241914]">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(currentStock || 99, q + 1))}
                    disabled={quantity >= currentStock || isOutOfStock}
                    className="w-8 h-9 flex items-center justify-center text-sm font-bold text-[#5E514A] hover:bg-[#E8DFD8] transition-colors disabled:opacity-40"
                  >
                    +
                  </button>
                </div>

                {/* Add To Cart Button */}
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className="flex-1 h-10 rounded-lg bg-[#C49A45] hover:bg-[#B08736] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <path d="M16 10a4 4 0 0 1-8 0"/>
                  </svg>
                  <span>{isOutOfStock ? 'Out of Stock' : 'Add to Cart'}</span>
                </button>

                {/* Wishlist Button */}
                <button
                  type="button"
                  onClick={() => toggleWishlist(product)}
                  className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 transition-colors ${
                    isSaved
                      ? 'bg-rose-50 border-rose-300 text-rose-600'
                      : 'bg-[#FAF8F5] border-[#D9D0C8] text-[#5E514A] hover:text-rose-600 hover:border-rose-300'
                  }`}
                  title={isSaved ? 'Remove from wishlist' : 'Save to wishlist'}
                >
                  <svg viewBox="0 0 24 24" className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"/>
                  </svg>
                </button>
              </div>

              {/* View Full Product Page Link */}
              <button
                type="button"
                onClick={handleOpenFullPage}
                className="w-full text-center py-2 text-[11px] font-bold uppercase tracking-wider text-[#9D772D] hover:text-[#7A5B1E] transition-colors block border border-dashed border-[#D9D0C8] rounded-lg hover:bg-[#FAF8F5]"
              >
                View Full Product Specifications & Details →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
